import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EnvDescriptor, EnvLoader } from '../Environment/EnvLoader';
import { BehaviorSubject } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { CookieService } from 'ngx-cookie-service';
import {
  EnterpriseResponse,
  FullAdmissionsData,
  GestionService,
  OrderHistoryObject,
} from './gestion.service';
import { QdmService } from './qdm.service';
import { TokenService } from './token.service';
import * as Sentry from '@sentry/angular';

export interface WalletHistory {
  createdAt: string;
  isCredit: boolean;
  currency: number;
}

export interface Wallet {
  currency: number;
  walletHistories: WalletHistory[];
}

export interface Company {
  id: number;
  name: string;
  reference: string;
  customerId: number;
  isAcceptPayment: boolean;
  menuTypes: any[];
  endOfServiceCategories: any[];
  wasteDatasCategories: any[];
  reservationDay: number;
  shoudHavePresenteeism: boolean;
  orderMaxHour: string;
  updateMaxHour: string;
  isUniqDayForOrder: boolean;
  orderMinWeek: number;
}

export interface UserCompanyData {
  id: number;
  companies: Company[];
  isIntern: boolean;
  isCanBook: boolean;
}

export interface User {
  id: number;
  email: string;
  badgeNumber: string;
  roles: string[];
  firstname: string;
  lastname: string;
  phone: string;
  wallet: Wallet;
  userCanteenData: string;
  userCompanyData: UserCompanyData;
  children: UserChildren[];
  preferences?: string;
  isSchoolMode: boolean;
}

export interface FoodProfile {
  id: number;
  name: string;
  code: string;
  customerId: number;
}

export interface SchoolInfo {
  id: number;
  name: string;
}

export interface UserChildren {
  id: number;
  firstname: string;
  lastname: string;
  birthdate: string;
  grade: string;
  mealMoment: string[];
  foodProfiles: FoodProfile[];
  customerId: number;
  schoolInfos: SchoolInfo;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private httpClient = inject(HttpClient);
  private cookie = inject(CookieService);
  private gestion = inject(GestionService);
  private qdm = inject(QdmService);
  private tokenService = inject(TokenService);

  env: EnvDescriptor | null = null;
  private isLoggedSubject = new BehaviorSubject<boolean>(false);
  public isLogged$ = this.isLoggedSubject.asObservable();
  public userSubject = new BehaviorSubject<User | undefined>(undefined);
  public user$ = this.userSubject.asObservable();
  public userEnterpriseSubject = new BehaviorSubject<EnterpriseResponse | null>(
    null
  );
  public userEnterprise$ = this.userEnterpriseSubject.asObservable();
  admissionsSubject = new BehaviorSubject<FullAdmissionsData[]>([]);
  public admissions$ = this.admissionsSubject.asObservable();

  public orderHistorySubject = new BehaviorSubject<OrderHistoryObject[]>([]);
  public orderHistory$ = this.orderHistorySubject.asObservable();

  /** Vues signal (lecture seule) des subjects ci-dessus. */
  /** Tokens présents (l'utilisateur peut être encore en cours de chargement) */
  readonly isLoggedIn = toSignal(this.isLogged$, { requireSync: true });
  readonly user = toSignal(this.user$, { requireSync: true });
  readonly userEnterprise = toSignal(this.userEnterprise$, { requireSync: true });

  constructor() {
    this.init();
  }

  /**
   * Initializes the service, setting up environment variables and authentication state
   * Verifies stored tokens, updates login status, and fetches user data if authenticated
   */
  init() {
    if ((globalThis as any).env) {
      this.env = (globalThis as any).env;
    } else {
      this.env = EnvLoader.load();
      (globalThis as any).env = this.env;
    }

    if (this.cookie.check('orderTokenBoGestion')) {
      this.tokenService.setTokenBoGestion(
        this.cookie.get('orderTokenBoGestion')
      );
    }

    if (this.cookie.check('orderRefreshTokenBoGestion')) {
      this.tokenService.setRefreshTokenBoGestion(
        this.cookie.get('orderRefreshTokenBoGestion')
      );
    }

    this.isLoggedSubject.next(this.isLogged());

    if (this.isLogged()) {
      const jwt = this.parseJwt(this.tokenService.getTokenBoGestion());
      const id = (jwt as any).id;

      if (this.userSubject.value && this.userSubject.value.id === id) return;
      this.fetchUserById(id).then((user) => {
        if (!user) {
          this.invalidateSession();
          return;
        }

        this.setUser(user as User);
        this.gestion
          .fetchUserEnterpriseData(user.id)
          .then((enterpriseData: EnterpriseResponse | null) => {
            this.userEnterpriseSubject.next(enterpriseData);
            if (enterpriseData) {
              this.gestion
                .fetchCommandsHistory(enterpriseData?.id)
                .then((orderHistory: OrderHistoryObject[]) => {
                  this.orderHistorySubject.next(orderHistory);
                });
            }
          });
      }).catch(() => this.invalidateSession());
    }
  }

  /**
   * Logs all service state information to console for debugging purposes
   * Displays tokens, login status, user data, and environment configuration as a table
   */
  printAll() {
    let data = {
      tokenBoGestion: this.tokenService.getTokenBoGestion(),
      refreshTokenBoGestion: this.tokenService.getRefreshTokenBoGestion(),
      isLogged: this.isLoggedSubject.value,
      user: JSON.stringify(this.userSubject.value),
      env: JSON.stringify(this.env),
    };

    // print all variables as table
    console.table(data);
  }

  /**
   * Sets the BO Gestion token and stores it in cookies
   * @param token - The authentication token to be stored
   */
  setTokenBoGestion(token: string) {
    this.cookie.set('orderTokenBoGestion', token, 365, '/');
    if (this.getBoGestionToken() !== token) {
      this.tokenService.setTokenBoGestion(token);
    }
  }

  /**
   * Sets the refresh token for BO Gestion and stores it in cookies
   * @param token - The refresh token to be stored
   */
  setRefreshTokenBoGestion(token: string) {
    this.cookie.set('orderRefreshTokenBoGestion', token, 365, '/');
    if (this.tokenService.getRefreshTokenBoGestion() !== token) {
      this.tokenService.setRefreshTokenBoGestion(token);
    }
  }

  /**
   * Retrieves the current BO Gestion authentication token
   * @returns The stored authentication token or undefined if not set
   */
  getBoGestionToken(): string | undefined {
    return this.tokenService.getTokenBoGestion();
  }

  /**
   * Checks if the user is currently authenticated
   * @returns Boolean indicating whether valid tokens exist
   */
  isLogged(): boolean {
    return (
      this.tokenService.getTokenBoGestion() !== '' &&
      this.tokenService.getRefreshTokenBoGestion() !== ''
    );
  }

  /**
   * Authenticates a user with email and password
   * @param email - User's email address
   * @param password - User's password
   * @returns Promise resolving to object with tokens, user data and enterprise data
   */
  login(
    email: string,
    password: string
  ): Promise<{
    token: string;
    refresh_token: string;
    user: User | undefined;
    entData: EnterpriseResponse | null;
  }> {
    return new Promise((resolve, reject) => {
      const boGestionUrl = this.env?.boGestionUrl;

      this.#post(`${boGestionUrl}/api/login`, {
        email: email,
        password: password,
      })
        .then((response: any) => {
          if (response.code) {
            this.logout();
            this.tokenService.setTokenBoGestion('');
            this.tokenService.setRefreshTokenBoGestion('');
            this.isLoggedSubject.next(false);
            reject(response.message);
            return;
          }

          if (response.token && response.refresh_token) {
            this.tokenService.setTokenBoGestion(response.token);
            this.tokenService.setRefreshTokenBoGestion(response.refresh_token);

            this.isLoggedSubject.next(true);

            const jwt = this.parseJwt(response.token);
            const id = (jwt as any).id;

            this.fetchUserById(id).then((user) => {
              if (user) {
                this.setUser(user as User);
                this.gestion
                  .fetchUserEnterpriseData(user.id)
                  .then((enterpriseData: EnterpriseResponse | null) => {
                    this.userEnterpriseSubject.next(enterpriseData);
                    resolve({
                      token: response.token,
                      refresh_token: response.refresh_token,
                      user: user as User | undefined,
                      entData: enterpriseData,
                    });
                  });
              }
            });
          }
        })
        .catch(reject);
    });
  }

  /**
   * Decodes and parses a JWT token
   * @param token - JWT token string to parse
   * @returns Decoded token payload or null if invalid
   */
  parseJwt(
    token: string
  ): { exp: number; iat: number; id: number; roles: string[] } | null {
    try {
      const payload = token.split('.')[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch (error) {
      console.error('Invalid JWT:', error);
      return null;
    }
  }

  /**
   * Fetches user data by user ID
   * @param id - The user ID to fetch
   * @returns Promise resolving to User object or undefined
   */
  fetchUserById(id: number): Promise<User | undefined> {
    return new Promise((resolve, reject) => {
      this.#get(`${this.env?.boGestionUrl}/api/users/${id}`)
        .then((response) => {
          resolve(response);
        })
        .catch(reject);
    });
  }

  /**
   * Checks if a user has access to a specific restaurant
   * @param restaurantId - ID of the restaurant to check access for
   * @param user - User object to verify authorization for
   * @returns Promise resolving to boolean indicating authorization status
   */
  isUserAuthorizedToRestaurant(
    restaurantId: number,
    user: User | undefined
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (user === undefined || this.env === null) {
        reject(false);
        return;
      }

      const email = user.email;

      const boGestionUrl = this.env?.boGestionUrl;

      this.#post(`${boGestionUrl}/api/login-by-company`, {
        restaurant: restaurantId,
        email: email,
      })
        .then((response) => {
          resolve(response.can_connect as boolean);
        })
        .catch(reject);
    });
  }

  /**
   * Session invalide (utilisateur introuvable, token expiré et refresh en échec) :
   * suppression des tokens sans réinitialiser la sélection de restaurant
   */
  private invalidateSession() {
    this.cookie.delete('orderTokenBoGestion', '/');
    this.cookie.delete('orderRefreshTokenBoGestion', '/');
    this.tokenService.setTokenBoGestion('');
    this.tokenService.setRefreshTokenBoGestion('');
    this.isLoggedSubject.next(false);
  }

  /**
   * Clears enterprise and admissions data from the service state
   */
  clearData() {
    this.userEnterpriseSubject.next(null);
    this.admissionsSubject.next([]);
  }

  /**
   * Logs out the current user
   * Removes tokens from cookies and service state, resets all user data
   */
  logout() {
    this.cookie.delete('orderTokenBoGestion', '/');
    this.cookie.delete('orderRefreshTokenBoGestion', '/');

    this.tokenService.setTokenBoGestion('');
    this.tokenService.setRefreshTokenBoGestion('');
    this.isLoggedSubject.next(false);
    this.setUser(undefined);
    this.userEnterpriseSubject.next(null);
    this.admissionsSubject.next([]);

    this.qdm.deleteAll();
  }

  /**
   * Updates the current user and sets Sentry user context
   * @param user - User object to set as current user or undefined to clear
   */
  setUser(user: User | undefined) {
    this.userSubject.next(user);
    let sentryUser: Sentry.User | null = null;

    if (user) {
      sentryUser = {
        id: user.id,
        email: user.email,
        username: `${user.firstname} ${user.lastname.toUpperCase()}`,
      };
    }

    Sentry.setUser(sentryUser);
  }

  /**
   * Makes a POST request to the specified endpoint
   * @param path - API endpoint URL
   * @param data - Request payload data
   * @returns Promise resolving to the API response
   * @private
   */
  #post(path: string, data: Record<string, any>): Promise<any> {
    return new Promise((resolve, reject) => {
      this.httpClient.post(path, data).subscribe({
        next: (response: any) => {
          resolve(response);
        },
        error: (error) => {
          if (error.error.code === 401) {
            return reject(new Error('Unauthorized'));
          }

          reject(error.message);
        },
      });
    });
  }

  /**
   * Makes an authenticated GET request to the specified endpoint
   * @param path - API endpoint URL
   * @returns Promise resolving to the API response
   * @private
   */
  #get(path: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .get(path, {
          headers: {
            Authorization: `Bearer ${this.tokenService.getTokenBoGestion()}`,
          },
        })
        .subscribe({
          next: (response: any) => resolve(response),
          error: reject,
        });
    });
  }
}
